// Module: ColumnSortButton
// -----------------------------------------------------------------
// Receives:  a column key and its label; the shared sort (SortContext)
// Produces:  the sort button in that column's header
// Owns:      nothing: the one active sort (or none) lives in
//            AnalyticsViewContent
// Does NOT:  combine sorts. The table sorts by one column at a time, so
//            choosing a column hands the sort over and every other column's
//            button falls back to its neutral arrow.
// -----------------------------------------------------------------
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { Key } from "../../constants/enums";
import { SORT_PRESETS, SortDirection } from "../../constants/config/sort";
import { useSort } from "../../models/SortContext";

/**
 * Input:   columnKey -- a column listed in COLUMN_SORT_KEYS; label -- its
 *          header text, for the accessible name.
 * Output:  a button that cycles this column through ascending, descending
 *          and no sorting; null when the column has no presets.
 */
export default function ColumnSortButton({
  columnKey,
  label,
}: {
  columnKey: Key;
  label: string;
}) {
  const { currentSort, onSelect } = useSort();
  const presets = SORT_PRESETS[columnKey] ?? [];
  if (presets.length === 0) return null;

  const isActive = currentSort?.key === columnKey;
  // Each press steps through the column's presets (both directions), then
  // clears the sort, then starts again: unsorted -> asc -> desc -> unsorted.
  const activeIndex = isActive
    ? presets.findIndex((preset) => preset.id === currentSort.id)
    : -1;
  const nextPreset =
    activeIndex < presets.length - 1 ? presets[activeIndex + 1] : null;
  const Icon = !isActive
    ? ArrowUpDown
    : currentSort.direction === SortDirection.ASC
      ? ArrowUp
      : ArrowDown;

  return (
    <button
      type="button"
      className="et-column-sort"
      data-active={isActive ? "true" : undefined}
      aria-pressed={isActive}
      // The name states the current sort (with aria-pressed); the title
      // says what the next press does.
      aria-label={
        isActive ? `Sort by ${label}, ${currentSort.label}` : `Sort by ${label}`
      }
      title={
        nextPreset
          ? `Sort by ${label}: ${nextPreset.label}`
          : `Clear sorting by ${label}`
      }
      onClick={() => onSelect(nextPreset)}
    >
      <Icon aria-hidden="true" />
    </button>
  );
}
